import { useEffect, useState } from 'react';
import usePlacesService from 'react-google-autocomplete/lib/usePlacesAutocompleteService';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormTextField } from '~/components/atoms/form-fields/FormTextField';
import Autocomplete from '~/components/ui/Autocomplete';
import { Button } from '~/components/ui/button';
import { GOOGLE_MAP_KEY } from '~/constants/common';
import { parsePlaceDetails } from '~/utils/google-maps/adress';

import { StyledAutocompleteContainer, StyledContainer, StyledDetailRow, StyledDetails } from './styled';

export const Address = () => {
  const { setValue, watch } = useFormContext();
  const { t } = useTranslation();
  const { placesService, placePredictions, getPlacePredictions } = usePlacesService({
    apiKey: GOOGLE_MAP_KEY,
  });

  const [isAddressWithDetails, setIsAddressWithDetails] = useState(false);
  const [fullAddress, setFullAddress] = useState('');

  const details = watch([
    'location.street',
    'location.postalCode',
    'location.number',
    'location.city',
    'location.state',
    'location.country',
  ]);

  const hasDetails = details.some((el) => !!el);

  useEffect(() => {
    if (hasDetails) {
      setIsAddressWithDetails(true);
    }
  }, [hasDetails]);

  const onDeleteAddress = () => {
    setIsAddressWithDetails(false);
    setValue('location.street', '');
    setValue('location.postalCode', '');
    setValue('location.number', '');
    setValue('location.city', '');
    setValue('location.state', '');
    setValue('location.country', '');
  };

  const onAddressSelect = (_, value) => {
    const selectedPlace = placePredictions.find((item) => item.description === value);

    placesService?.getDetails({ placeId: selectedPlace.place_id }, (placeDetails) => {
      setIsAddressWithDetails(true);
      const { city, country, postalCode, state, street, number } = parsePlaceDetails(placeDetails);

      setValue('location.street', street);
      setValue('location.postalCode', postalCode);
      setValue('location.number', number);
      setValue('location.city', city);
      setValue('location.state', state);
      setValue('location.country', country);
    });
  };

  return (
    <StyledContainer>
      <StyledAutocompleteContainer>
        <Autocomplete
          fullWidth
          freeSolo
          disableClearable
          disabled={isAddressWithDetails}
          options={placePredictions.map((option) => option.description)}
          onChange={onAddressSelect}
          value={fullAddress}
          label={t('template5:locationOpt')}
          onTextInputChange={(event) => {
            getPlacePredictions({ input: event.target.value });
            setFullAddress(event.target.value);
          }}
        />
        <Button
          type="primary-outlined"
          size="xs"
          sx={{ whiteSpace: 'nowrap', height: 'fit-content' }}
          onClick={isAddressWithDetails ? onDeleteAddress : () => setIsAddressWithDetails(!isAddressWithDetails)}
        >
          {isAddressWithDetails ? t('template5:delete_address') : t('template5:manual')}
        </Button>
      </StyledAutocompleteContainer>
      {isAddressWithDetails && (
        <StyledDetails>
          <StyledDetailRow>
            <FormTextField
              name="location.street"
              fullWidth
              label={t('template5:street')}
              inputProps={{ maxLength: 70 }}
            />
            <FormTextField
              name="location.number"
              fullWidth
              label={t('template5:strNumber')}
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 40 }}
            />
            <FormTextField
              name="location.postalCode"
              fullWidth
              label={t('template5:postCode')}
              inputProps={{ maxLength: 40 }}
            />
          </StyledDetailRow>
          <StyledDetailRow>
            <FormTextField name="location.city" fullWidth label={t('template5:city')} inputProps={{ maxLength: 70 }} />
            <FormTextField
              name="location.state"
              fullWidth
              label={t('template5:state')}
              inputProps={{ maxLength: 70 }}
            />
          </StyledDetailRow>
          <FormTextField
            name="location.country"
            fullWidth
            label={t('template5:country')}
            inputProps={{ maxLength: 70 }}
          />
        </StyledDetails>
      )}
    </StyledContainer>
  );
};
